import { DOCUMENT } from '@angular/common';
import { DestroyRef, Directive, inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { fromEvent } from 'rxjs';

/**
 * Keeps recursive sidebar flyout menus inside the caged `.main-app-content` overlay
 * (which has `overflow: hidden`, see app-cdk-config.service.ts). CDK doesn't constrain
 * mat-menu panels here, so deep submenus opened near the bottom get clipped.
 *
 * On open we cap each panel's height to the workspace (so it scrolls) and translate the
 * overlay host back into view. We shift the host — not the panel, whose `transform` is
 * owned by the enter animation. CDK applies the menu position in an `afterNextRender` that
 * runs *after* `menuOpened`, so the rAF pass (which measures the positioned pane) does the
 * real correction; both passes land before the browser paints, so there is no flash.
 */
@Directive({
  selector: '[appMenuFlyoutFit]',
})
export class MenuFlyoutFitDirective {
  private static readonly VIEWPORT_MARGIN = 0;
  private static readonly PANEL_SELECTOR = '.menu-flyout.mat-mdc-menu-panel';
  private static readonly PANE_SELECTOR = '.cdk-overlay-pane';
  private static readonly HOST_SELECTOR = '.cdk-overlay-connected-position-bounding-box';

  private readonly trigger = inject(MatMenuTrigger);
  private readonly doc = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.trigger.menuOpened.pipe(takeUntilDestroyed()).subscribe(() => {
      // Early pass (height cap is position-independent); the rAF pass corrects the position
      // once CDK has placed the pane in its deferred afterNextRender.
      this.fitAll();
      this.zone.runOutsideAngular(() => requestAnimationFrame(() => this.fitAll()));
    });

    this.zone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.trigger.menuOpen) this.fitAll();
        });
    });
  }

  private fitAll(): void {
    const ws = this.doc.querySelector('.main-app-content')?.getBoundingClientRect();
    if (!ws) return;

    const margin = MenuFlyoutFitDirective.VIEWPORT_MARGIN;
    const maxAllowed = ws.height - 2 * margin;

    this.doc.querySelectorAll<HTMLElement>(MenuFlyoutFitDirective.PANEL_SELECTOR).forEach(panel => {
      const pane = panel.closest<HTMLElement>(MenuFlyoutFitDirective.PANE_SELECTOR);
      const host = panel.closest<HTMLElement>(MenuFlyoutFitDirective.HOST_SELECTOR);
      if (!pane || !host) return;

      // Reset prior adjustments so we measure the natural box.
      panel.style.maxHeight = '';
      panel.style.overflowY = '';
      host.style.transform = '';

      if (pane.getBoundingClientRect().height > maxAllowed) {
        panel.style.maxHeight = `${maxAllowed}px`;
        panel.style.overflowY = 'auto';
      }

      // Measure the pane (unaffected by the panel's scale animation), then shift into view.
      const rect = pane.getBoundingClientRect();
      const overflowBottom = rect.bottom - (ws.bottom - margin);
      const overflowTop = ws.top + margin - rect.top;
      const shift = overflowBottom > 0 ? -overflowBottom : overflowTop > 0 ? overflowTop : 0;
      if (shift !== 0) {
        host.style.transform = `translateY(${Math.round(shift)}px)`;
      }
    });
  }
}
